import { COLORS } from '@/constants/colors';
import { createTheme } from '@mui/material';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    'outline-primary': true;
    'outline-secondary': true;
    'form-button': true;
    secondary: true;
    transparent: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsVariantOverrides {
    primary: true;
  }
}

export const THEME = createTheme({
  typography: {
    fontFamily: `var(--font-urbanist), var(--font-roboto), "Helvetica", "Arial", sans-serif`,
  },
  palette: {
    primary: {
      main: COLORS['primary/500'],
    },
    secondary: {
      main: COLORS['secondary/500'],
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& .MuiOutlinedInput-root': {
            '& input[type=number]': {
              MozAppearance: 'textfield',
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: ownerState.error ? COLORS.red : COLORS.orange,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ownerState.error ? COLORS.red : COLORS.orange,
              borderWidth: '1px',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: ownerState.error ? COLORS.red : COLORS['neutral/300'],
          },
          '& .MuiInputLabel-outlined': {
            color: ownerState.error ? COLORS.red : undefined,

            '&.MuiInputLabel-shrink': {
              color: ownerState.error ? COLORS.red : COLORS.orange,

              '&.Mui-disabled': {
                color: COLORS['neutral/300'],
              },
            },
          },
          '& .MuiFormHelperText-root': {
            color: ownerState.error ? COLORS.red : undefined,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: ownerState.error ? COLORS.red : COLORS.orange,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: ownerState.error ? COLORS.red : COLORS.orange,
            borderWidth: '1px',
          },
        }),
        notchedOutline: ({ ownerState }) => ({
          borderColor: ownerState.error ? COLORS.red : COLORS['neutral/300'],
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: ownerState.error ? COLORS.red : COLORS.gray,
          '&.Mui-focused': {
            color: ownerState.error ? COLORS.red : COLORS.orange,
          },
          '&.MuiInputLabel-shrink': {
            backgroundColor: `${COLORS['neutral/50'] || 'white'} !important`,
            paddingLeft: '2px',
            paddingRight: '2px',
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: ownerState.error ? COLORS.red : COLORS.gray,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: ({ ownerState }) => ({
          color: ownerState.error ? COLORS.red : COLORS.orange,
          right: '10px',
        }),
        select: {
          paddingRight: '36px !important',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          gap: '8px',
          borderRadius: '6px',
          textTransform: 'capitalize',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50px',
          fontWeight: 600,
          minWidth: 'auto',

          '& .MuiButton-startIcon': {
            margin: 0,
          },

          '& .MuiButton-endIcon': {
            margin: 0,
          },
          '&:disabled': {
            background: '#EBEBEB !important',
            color: '#404040 !important',
          },
        },
      },
      variants: [
        {
          props: { variant: 'primary' },
          style: () => ({
            background: '#647DAF',
            color: COLORS.white,
            '&:hover': {
              background: '#40557D',
            },
          }),
        },
        {
          props: { variant: 'outline-primary' },
          style: () => ({
            background: COLORS.white,
            color: '#4F689A',
            border: '2px solid #647DAF',
            path: {
              fill: `${COLORS['secondary/500']}`,
              stroke: 'none',
            },
            '&:hover': {
              background: '#647DAF',
              color: COLORS.white,

              path: {
                fill: `${COLORS.white}`,
                stroke: 'none',
              },
            },
          }),
        },
        {
          props: { variant: 'form-button' },
          style: () => ({
            background: COLORS.white,
            color: '#0A0A0A',
            border: `1px solid ${COLORS['neutral/500']}`,
            '&:hover': {
              border: `1px solid ${COLORS.orange}`,
            },
            '&:disabled': {
              path: {
                fill: `transparent !important`,
                stroke: `${COLORS['neutral/500']} !important`,
              },
            },
          }),
        },
        {
          props: { variant: 'outline-secondary' },
          style: () => ({
            background: 'transparent',
            color: COLORS['secondary/800'],
            border: `2px solid ${COLORS['secondary/500']}`,
            '&:hover': {
              background: COLORS['secondary/700'],
              color: COLORS['secondary/100'],
              border: `2px solid ${COLORS['secondary/700']}`,

              path: {
                fill: COLORS['secondary/100'],
              },
            },
          }),
        },
        {
          props: { variant: 'secondary' },
          style: () => ({
            background: COLORS['secondary/500'],
            color: COLORS['white'],
            '&:hover': {
              background: COLORS['secondary/700'],
              color: COLORS.white,
            },
          }),
        },
        {
          props: { variant: 'transparent' },
          style: () => ({
            background: 'transparent',
            color: '#4F689A',
            padding: '10px',
            width: 'fit-content',
            height: 'fit-content',
            '&:hover': {
              background: 'transparent',
              color: '#4F689A',
            },
          }),
        },
      ],
    },
  },
});
